import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { UseMutateFunction } from 'react-query'
import { AxiosResponse } from 'axios'
import { FiChevronDown, FiLock } from 'react-icons/fi'
import { faFull, faPlain, toman } from 'utilities/helper/format'
import { demandPriority } from 'utilities/helper/demandPriority'
import Detail from './detail'
import DeleteModal from './detail/modal'
import { initialOf, waitedLabel } from '../../../components/format'

interface IPayload {
    id: string | undefined
    loanId: number
    note?: string
}
interface IItemProps {
    item: IDemandResult
    isLoading: boolean
    mutate: UseMutateFunction<
        AxiosResponse<any, any>,
        unknown,
        IPayload,
        unknown
    >
    isManager?: boolean
    /** رتبهٔ اعتباری, when the queue is sorted by it */
    rank?: number
}
interface IModal {
    show: boolean
    loanId: number
}

/**
 * One loan request in the queue.
 *
 * The card answers the four things a manager scans a queue for — who, how
 * much, how urgently, and where the managers stand — and nothing else; the
 * member's numbers are one tap down, and the request itself (its thread, its
 * votes, the grant) is the کارتابل the card opens.
 *
 * The note is shown only to managers and to the requester: the request form
 * promises the member «این پیام فقط برای مدیران قابل مشاهده است», and a fund
 * with «نمایش درخواست‌های دیگران» on shows this list to every member.
 */
export default function Item({
    item,
    isLoading,
    mutate,
    isManager = false,
    rank,
}: IItemProps) {
    const navigate = useNavigate()
    const { id: fundId } = useParams()
    const [factsOpen, setFactsOpen] = useState(false)
    const [modal, setModal] = useState<IModal>({ show: false, loanId: 0 })

    const name = item.poolship?.name ?? ''
    const priority = demandPriority(item.demand_priority)
    const perInstallment =
        item.num_repayments > 0
            ? Math.round(Number(item.amount || 0) / item.num_repayments)
            : 0
    const filedByManager =
        !!item.registrar &&
        !!item.poolship &&
        item.registrar.id !== item.poolship.id
    const agreeCount = item.liked?.length ?? 0
    const disagreeCount = item.disliked?.length ?? 0
    const myStance = item.you_liked
        ? 'agree'
        : item.you_disliked
        ? 'disagree'
        : null
    const canReadNote = isManager || item.is_poolship_requsted_loan
    const note = canReadNote ? item.user_description?.trim() : ''

    const openCartable = () => {
        posthog.capture('loan_demand_cartable_opened', {
            fund_id: fundId,
            demand_id: item.id,
            source: 'requests_list',
            is_manager: isManager,
            my_stance: isManager ? myStance : undefined,
        })
        navigate(`/fund/loan-request/detail/${item.id}?fund=${fundId}`)
    }

    return (
        <article className=" overflow-hidden rounded-2xl border border-stroke bg-white">
            <div
                role="button"
                tabIndex={0}
                aria-label={`درخواست وام ${name}`}
                onClick={openCartable}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openCartable()
                    }
                }}
                className=" cursor-pointer p-4 active:bg-canvas"
            >
                <div className=" flex items-start gap-3">
                    <span
                        aria-hidden
                        className=" flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-bold text-brand-deep"
                    >
                        {initialOf(name)}
                    </span>
                    <div className=" min-w-0 flex-1">
                        <div className=" flex items-start justify-between gap-2">
                            <p className=" truncate text-sm font-bold text-heading">
                                {name}
                            </p>
                            {priority && (
                                <span
                                    className={` shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${priority.chipClass}`}
                                >
                                    {priority.label}
                                </span>
                            )}
                        </div>
                        <p className=" mt-[2px] text-[11px] text-text-muted">
                            {waitedLabel(item.created)} ثبت شد
                            {item.index
                                ? `، نوبت ${faPlain.format(item.index)}`
                                : ''}
                            {rank ? `، رتبهٔ ${faPlain.format(rank)}` : ''}
                        </p>
                    </div>
                </div>

                <div className=" mt-3 rounded-xl bg-canvas px-3 py-2.5">
                    <p className=" text-base font-bold text-heading">
                        {toman(item.amount)}
                    </p>
                    <p className=" mt-[2px] text-[11px] text-text-secondary">
                        {faPlain.format(item.num_repayments)} قسط
                        {perInstallment > 0 &&
                            `، هر قسط حدود ${faFull.format(
                                perInstallment
                            )} تومان`}
                    </p>
                </div>

                {priority && (
                    <p className=" mt-2 text-[11px] text-text-secondary">
                        {priority.need}
                    </p>
                )}

                {note && (
                    <p className=" mt-2 line-clamp-2 whitespace-pre-line text-[11px] leading-6 text-text-body">
                        «{note}»
                    </p>
                )}

                {filedByManager && (
                    <p className=" mt-2 text-[11px] text-text-muted">
                        ثبت‌شده توسط {item.registrar.name} از طرف عضو
                    </p>
                )}
            </div>

            {/* The managers' vote. Managers only — the server sends members
                an empty list, and the lock says so. */}
            {isManager && (
                <div className=" flex items-center justify-between gap-2 border-t border-divider px-4 py-2.5">
                    <span className=" flex min-w-0 items-center gap-1.5 text-[11px] text-text-secondary">
                        <FiLock
                            size={11}
                            aria-label="فقط مدیران می‌بینند"
                            className=" shrink-0"
                        />
                        {agreeCount === 0 && disagreeCount === 0 ? (
                            'هنوز مدیری نظر نداده'
                        ) : (
                            <span>
                                {agreeCount > 0 && (
                                    <span className=" font-bold text-success-on">
                                        {faPlain.format(agreeCount)} موافق
                                    </span>
                                )}
                                {agreeCount > 0 && disagreeCount > 0 && '، '}
                                {disagreeCount > 0 && (
                                    <span className=" font-bold text-danger-on">
                                        {faPlain.format(disagreeCount)} مخالف
                                    </span>
                                )}
                            </span>
                        )}
                    </span>
                    <span
                        className={` shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
                            myStance === 'agree'
                                ? ' bg-success-2 text-success-on'
                                : myStance === 'disagree'
                                ? ' bg-danger-soft text-danger-on'
                                : ' bg-warning-soft text-warning-on'
                        }`}
                    >
                        {myStance === 'agree'
                            ? 'شما موافقید'
                            : myStance === 'disagree'
                            ? 'شما مخالفید'
                            : 'منتظر نظر شما'}
                    </span>
                </div>
            )}

            <div className=" flex border-t border-divider text-[12px] font-bold">
                <button
                    type="button"
                    aria-expanded={factsOpen}
                    onClick={() => {
                        if (!factsOpen) {
                            posthog.capture('loan_demand_facts_opened', {
                                fund_id: fundId,
                                demand_id: item.id,
                            })
                        }
                        setFactsOpen(!factsOpen)
                    }}
                    className=" flex flex-1 items-center justify-center gap-1 py-3 text-text-secondary"
                >
                    مشخصات عضو
                    <FiChevronDown
                        size={14}
                        aria-hidden
                        className={` transition-transform ${
                            factsOpen ? ' rotate-180' : ''
                        }`}
                    />
                </button>
                <span aria-hidden className=" my-2 w-px bg-divider" />
                <button
                    type="button"
                    onClick={openCartable}
                    className=" flex-1 py-3 text-green-1"
                >
                    کارتابل و گفت‌وگو ←
                </button>
            </div>

            {factsOpen && (
                <div className=" border-t border-divider px-4 pb-4">
                    <Detail
                        item={item}
                        isLoading={isLoading}
                        isManager={isManager}
                        setModal={setModal}
                    />
                </div>
            )}

            <DeleteModal
                type="submit"
                onHide={() => setModal({ ...modal, show: false })}
                show={modal.show}
                mutate={mutate}
                loanId={modal.loanId}
                // a manager reading their OWN request is its requester here:
                // cancelling it is not a rejection
                asManager={isManager && !item.is_poolship_requsted_loan}
            />
        </article>
    )
}
